import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ChevronDown, ChevronRight, Layers, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AreaReviewList } from "./AreaReviewList";
import { AreaProposals } from "./AreaProposals";
import { AreaOverrideForm } from "./AreaOverrideForm";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

/** Therapeutic-area taxonomy health — Other %, unmapped labels, AI proposals, manual overrides, "Refresh now". */
export function AreaHealthPanel({ onChanged }) {
    const [open, setOpen] = useState(true);
    const [health, setHealth] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/v1/ripeness/areas/health");
            setHealth(r.data);
        } catch (e) { /* admin-only panel */ }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, health?.running ? 5000 : 30000);
        return () => clearInterval(id);
    }, [load, health?.running]);

    const changed = useCallback(async () => { await load(); onChanged?.(); }, [load, onChanged]);

    const refreshNow = useCallback(async () => {
        setRefreshing(true);
        try {
            const r = await api.post("/v1/ripeness/areas/refresh", {});
            const where = r.data.forwarded ? ` on the hub (${r.data.processed_on}) — results sync back shortly` : "";
            if (r.data.status === "already_running") toast.info(`A refresh is already running${where} — another one is queued`);
            else toast.success(`Area refresh started${r.data.run_id ? ` (${r.data.run_id})` : ""}${where}`);
            setTimeout(load, 1500);
        } catch (e) {
            toast.error(`Refresh failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setRefreshing(false);
        }
    }, [load]);

    const last = health?.last_run;
    const running = !!health?.running;
    const areas = health?.taxonomy?.areas || [];
    return (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="area-health-panel">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 mb-2" data-testid="area-health-toggle">
                <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Therapeutic areas
                    {health ? <span className="text-slate-600 normal-case tracking-normal" data-testid="area-health-total">({health.taxonomy.total})</span> : null}
                </span>
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            {open && health ? (
                <div className="text-xs space-y-2">
                    <div className="text-slate-500" data-testid="area-health-summary">
                        <span className="text-slate-300">{health.taxonomy.canonical}</span> canonical
                        {health.taxonomy.custom.length ? <> + <span className="text-amber-200" data-testid="area-health-custom">{health.taxonomy.custom.length} custom</span></> : null}
                        {" · "}Other <span className={health.other.pct > 2 ? "text-amber-300" : "text-slate-300"} data-testid="area-health-other">{health.other.count} ({health.other.pct}%)</span>
                        {" · "}fields v{health.fields_version}
                    </div>
                    <div className="text-slate-500" data-testid="area-health-ai">
                        <Sparkles className="w-3 h-3 inline mr-1 text-violet-300" />
                        AI classifier {health.ai.enabled ? <span className="text-emerald-300">on</span> : <span className="text-rose-300">off</span>}
                        {" "}· auto-apply ≥ <span className="text-slate-300">{Math.round(health.ai.min_confidence * 100)}%</span>
                        {" · "}daily <code className="text-slate-400">{health.schedule.cron.expression}</code> UTC + after every job
                    </div>
                    {health.managed_here === false ? (
                        <div className="text-[11px] text-sky-300/80" data-testid="area-health-hub-note">Classified on the hub — refreshes and overrides you start here are carried out there and arrive by Radar Sync.</div>
                    ) : null}
                    {last ? (
                        <div className="border border-slate-800 rounded p-2" data-testid="area-last-run">
                            <div className="flex items-center gap-2">
                                <span className={`rounded-full border px-2 py-0.5 uppercase tracking-wider ${
                                    last.status === "done" ? "border-emerald-400/40 text-emerald-300"
                                        : last.status === "failed" ? "border-rose-400/40 text-rose-300"
                                            : running ? "border-sky-400/40 text-sky-300" : "border-slate-500/30 text-slate-400"}`} data-testid="area-last-run-status">
                                    {running ? <RefreshCw className="w-3 h-3 inline mr-1 animate-spin" /> : null}{last.status}
                                </span>
                                <span className="text-slate-400 font-mono truncate">{last.run_id}</span>
                                <span className="ml-auto text-slate-500">{last.trigger}</span>
                            </div>
                            <div className="text-slate-500 mt-1" data-testid="area-last-run-counts">
                                {last.labels_considered} labels · <span className="text-emerald-300">{last.applied} applied</span> · {last.review} review · {last.residual} residual
                                {last.proposals ? <> · <span className="text-amber-200">{last.proposals} proposed</span></> : null}
                                {" · "}{last.tracks_reprojected} tracks · ${Number(last.cost_usd || 0).toFixed(3)}
                            </div>
                            <div className="text-slate-600 mt-0.5">{fmt(last.finished_at || last.started_at)}{last.error ? <span className="text-rose-300"> · {last.error}</span> : null}</div>
                        </div>
                    ) : <div className="text-slate-500 italic" data-testid="area-no-run">No refresh run yet.</div>}
                    <div className="flex items-center gap-2 pt-1">
                        <button onClick={refreshNow} disabled={refreshing || running}
                            title={running ? "A refresh is running" : "Classify unmapped indications now"}
                            className="inline-flex items-center gap-1 rounded bg-violet-500/80 hover:bg-violet-500 px-2 py-1 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="area-refresh-btn">
                            <RefreshCw className={`w-3 h-3 ${refreshing || running ? "animate-spin" : ""}`} /> Refresh now
                        </button>
                        <span className="text-slate-500" data-testid="area-unmapped-counts">
                            {health.unmapped.pending} pending · {health.unmapped.review} review · {health.unmapped.proposed} proposed · {health.unmapped.residual} residual
                        </span>
                    </div>
                    <AreaProposals proposals={health.proposals} onChanged={changed} />
                    <AreaReviewList items={health.unmapped.items} areas={areas} onChanged={changed} />
                    <AreaOverrideForm areas={areas} onChanged={changed} />
                </div>
            ) : null}
        </div>
    );
}
